import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Box,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useUserQuery } from "../../../../hooks/api_hooks/useUserQuery";
import { useQueryClient } from "@tanstack/react-query";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DrawIcon from "@mui/icons-material/Draw";
import LockIcon from "@mui/icons-material/Lock";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

/* ─── Load signature font ─── */
(() => {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap";
  document.head.appendChild(link);
})();

const PRIVACY_LAWS = [
  "CCPA/CPRA (California)",
  "VCDPA (Virginia)",
  "CPA (Colorado)",
  "CTDPA (Connecticut)",
  "UCPA (Utah)",
  "TDPSA (Texas)",
  "FDBR (Florida)",
  "MCDPA (Montana)",
  "OCPA (Oregon)",
  "TIPA (Tennessee)",
  "IDPL (Indiana)",
  "ICDPA (Iowa)",
  "DPDPA (Delaware)",
  "New Hampshire Privacy Act",
  "New Jersey Privacy Act",
  "Nebraska Data Privacy Act",
  "Maryland Online Data Privacy Act",
  "Minnesota Consumer Data Privacy Act",
  "Rhode Island Data Transparency Act",
  "Quebec Law 25 (Canada)",
];

const Authorization = () => {
  const { data, isLoading, isFetching } = useUserQuery();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [signature, setSignature] = useState("");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const printRef = useRef(null);

  const userRecord = useMemo(() => data?.user || {}, [data]);
  const alreadySigned = !!userRecord?.authorization_signed;

  const personalInfo = useMemo(
    () =>
      alreadySigned
        ? userRecord?.personal_information_signed || {}
        : userRecord?.personal_information || {},
    [alreadySigned, userRecord],
  );

  const today = useMemo(() => {
    return new Date().toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (
      !alreadySigned &&
      (!userRecord?.personal_information ||
        Object.keys(userRecord.personal_information).length === 0)
    ) {
      navigate("/profile/personal-information", { replace: true });
    }
  }, [isLoading, isFetching, alreadySigned, userRecord, navigate]);

  const isReady = !isLoading && !isFetching && !!userRecord;
  if (!isReady) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const {
    firstName = "",
    lastName = "",
    streetAddress = "",
    unitNumber = "",
    city = "",
    state = "",
    zipCode = "",
  } = personalInfo;

  const fullName = `${firstName} ${lastName}`.trim();
  const formattedAddress = [streetAddress, unitNumber, city, state, zipCode]
    .filter(Boolean)
    .join(", ");

  const signatureMatches = signature.trim() === fullName && fullName !== "";

  const signedDate = alreadySigned
    ? new Date(userRecord.authorization_signed_at * 1000).toLocaleDateString(
        "en-US",
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        },
      )
    : today;

  const handleSign = async () => {
    if (!signatureMatches) {
      setSnack({
        open: true,
        message: "Signature must match your full name exactly.",
        severity: "error",
      });
      return;
    }

    const payload = {
      authorization_signed: true,
      authorization_signed_at: Math.floor(new Date().getTime() / 1000), // Unix seconds
      personal_information: userRecord?.personal_information,
    };

    try {
      setSaving(true);
      const token = user?.access_token;
      if (!token) throw new Error("Missing access token");

      const res = await fetch(
        "https://api.kloudscan.com/authorization-signed",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) throw new Error(`Failed with ${res.status}`);

      queryClient.setQueryData(["user"], (oldData) => {
        if (!oldData?.user) return oldData;
        return {
          ...oldData,
          user: {
            ...oldData.user,
            authorization_signed: true,
            authorization_signed_at: payload.authorization_signed_at,
            personal_information_signed: payload.personal_information,
          },
        };
      });

      await queryClient.invalidateQueries({
        queryKey: ["user"],
        refetchType: "inactive",
      });

      setSnack({
        open: true,
        message: "Document signed successfully!",
        severity: "success",
      });
      setTimeout(() => {
        setSnack((s) => ({ ...s, open: false }));
        navigate("/dashboard", { replace: true });
      }, 1500);
    } catch (err) {
      console.error("Error submitting authorization:", err);
      setSnack({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
            fontWeight: 600,
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <Box ref={printRef}>
        {/* ── Top status bar ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: alreadySigned ? "#f0fdf4" : "#F0F9FF",
            border: `1px solid ${alreadySigned ? "#bbf7d0" : "#BAE6FD"}`,
            borderRadius: "12px",
            px: 2.5,
            py: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <DescriptionOutlinedIcon
              sx={{
                fontSize: 18,
                color: alreadySigned ? "#166534" : "#09b2f0",
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  color: alreadySigned ? "#166534" : "#0F172A",
                }}
              >
                Authorization to Appoint Kloud Scan Inc
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: alreadySigned ? "#15803d" : "#64748B",
                }}
              >
                {alreadySigned
                  ? `Signed on ${signedDate}`
                  : "Signature required"}
              </Typography>
            </Box>
          </Box>
          {alreadySigned ? (
            <Chip
              icon={
                <CheckCircleIcon
                  sx={{
                    fontSize: "14px !important",
                    color: "#166534 !important",
                  }}
                />
              }
              label="Completed"
              size="small"
              sx={{
                backgroundColor: "#dcfce7",
                color: "#166534",
                fontWeight: 600,
                fontSize: "0.72rem",
                border: "1px solid #bbf7d0",
                borderRadius: "6px",
                height: 26,
              }}
            />
          ) : (
            <Chip
              icon={
                <DrawIcon
                  sx={{
                    fontSize: "13px !important",
                    color: "#0369a1 !important",
                  }}
                />
              }
              label="Pending signature"
              size="small"
              sx={{
                backgroundColor: "#e0f2fe",
                color: "#0369a1",
                fontWeight: 600,
                fontSize: "0.72rem",
                border: "1px solid #bae6fd",
                borderRadius: "6px",
                height: 26,
              }}
            />
          )}
        </Box>

        {/* ── Document card ── */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          {/* Document header */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: "1px solid #F1F5F9",
              backgroundColor: "#FAFBFD",
            }}
          >
            <Typography
              sx={{ fontSize: "1rem", fontWeight: 700, color: "#0F172A" }}
            >
              Authorization Agreement
            </Typography>
            <Typography
              sx={{ fontSize: "0.78rem", color: "#94A3B8", mt: 0.25 }}
            >
              Date: {signedDate}
            </Typography>
          </Box>

          {/* Document body */}
          <Box sx={{ px: 3, py: 3 }}>
            {/* Opening paragraph */}
            <Typography
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.8,
                color: "#334155",
                mb: 2,
              }}
            >
              I, <strong>{fullName || "[Your Name]"}</strong>, residing at{" "}
              <strong>{formattedAddress || "[Your Address]"}</strong>, hereby
              appoint <strong>Kloud Scan Inc.</strong> to act as my authorized
              agent in exercising my rights under applicable privacy and data
              protection laws.
            </Typography>

            {/* Laws paragraph */}
            <Typography
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.8,
                color: "#334155",
                mb: 2,
              }}
            >
              This authorization applies to all applicable privacy legislation,
              including but not limited to:{" "}
              {PRIVACY_LAWS.map((law, i) => (
                <React.Fragment key={i}>
                  <strong>{law}</strong>
                  {i < PRIVACY_LAWS.length - 1 ? ", " : "."}
                </React.Fragment>
              ))}
            </Typography>

            {/* Scope paragraph */}
            <Typography
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.8,
                color: "#334155",
                mb: 2,
              }}
            >
              Under this authorization, <strong>Kloud Scan, Inc.</strong> is
              permitted to submit deletion and erasure requests on my behalf,
              withdraw consent for the processing of my personal data, and
              object to the use or sale of my information to third parties.
            </Typography>

            {/* Understanding paragraph */}
            <Typography
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.8,
                color: "#334155",
                mb: 3,
              }}
            >
              I understand that <strong>Kloud Scan, Inc.</strong> may cease
              acting as my authorized agent at any time, that this authorization
              concludes once a given request has been fulfilled, and that I
              retain the right to revoke this authorization at any time by
              providing written notice.
            </Typography>

            {/* ── Signature section ── */}
            <Box sx={{ borderTop: "1px solid #F1F5F9", pt: 3 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <DrawIcon sx={{ fontSize: 14, color: "#09b2f0" }} />
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#94A3B8",
                  }}
                >
                  {alreadySigned ? "Signature" : "Sign Here"}
                </Typography>
              </Box>
              <Box sx={{ height: "1px", backgroundColor: "#F1F5F9", mb: 2 }} />

              {alreadySigned ? (
                <Box
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "10px",
                    p: 2.5,
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Dancing Script', cursive",
                      fontSize: "2.2rem",
                      fontWeight: 600,
                      color: "#1E293B",
                      lineHeight: 1.3,
                      mb: 1,
                    }}
                  >
                    {fullName}
                  </Typography>
                  <Box
                    sx={{
                      height: "1px",
                      width: 200,
                      backgroundColor: "#E2E8F0",
                      mb: 1,
                    }}
                  />
                  <Typography sx={{ fontSize: "0.78rem", color: "#64748B" }}>
                    {fullName}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mt: 1.5,
                    }}
                  >
                    <LockIcon sx={{ fontSize: 12, color: "#09b2f0" }} />
                    <Typography sx={{ fontSize: "0.72rem", color: "#64748B" }}>
                      Electronically signed · {signedDate}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  <Typography
                    sx={{ fontSize: "0.82rem", color: "#64748B", mb: 2 }}
                  >
                    Type your full name —{" "}
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: "#0F172A" }}
                    >
                      {fullName}
                    </Box>{" "}
                    — to sign.
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder={fullName}
                    variant="outlined"
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    error={Boolean(signature) && !signatureMatches}
                    helperText={
                      signature && !signatureMatches
                        ? "Must match your full name exactly."
                        : " "
                    }
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "#F8FAFC",
                        fontSize: "0.9rem",
                        "&:hover fieldset": { borderColor: "#09b2f0" },
                        "&.Mui-focused": {
                          backgroundColor: "#fff",
                          boxShadow: "0 0 0 3px rgba(9,178,240,0.12)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#09b2f0",
                          borderWidth: "1.5px",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#09b2f0",
                      },
                    }}
                  />

                  {/* Live preview box */}
                  <Box
                    sx={{
                      border: `1.5px dashed ${signatureMatches ? "#09b2f0" : "#E2E8F0"}`,
                      borderRadius: "10px",
                      px: 2.5,
                      py: 2,
                      mb: 3,
                      backgroundColor: signatureMatches ? "#F0F9FF" : "#FAFBFD",
                      transition: "all 0.2s ease",
                      minHeight: 80,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Dancing Script', cursive",
                        fontSize: "2.2rem",
                        fontWeight: 600,
                        color: signature ? "#1E293B" : "#CBD5E1",
                        lineHeight: 1.3,
                        transition: "color 0.2s ease",
                      }}
                    >
                      {signature || fullName}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <LockIcon sx={{ fontSize: 13, color: "#94A3B8" }} />
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "#94A3B8" }}
                      >
                        Legally binding electronic signature
                      </Typography>
                    </Box>

                    <Button
                      onClick={handleSign}
                      disabled={!signatureMatches || saving}
                      startIcon={
                        saving ? (
                          <CircularProgress size={14} sx={{ color: "#fff" }} />
                        ) : (
                          <DrawIcon sx={{ fontSize: "15px !important" }} />
                        )
                      }
                      sx={{
                        backgroundColor:
                          !signatureMatches || saving ? "#E2E8F0" : "#09b2f0",
                        color: !signatureMatches || saving ? "#94A3B8" : "#fff",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2.5,
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor:
                            !signatureMatches || saving ? "#E2E8F0" : "#07a0d9",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {saving ? "Saving…" : "Sign Document"}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Authorization;
